import { datadogRum } from '@datadog/browser-rum'
import type { RecordClinicianActivityRequest } from '@seen/request-types/recordClinicianActivity'
import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import {
  ActivityCollector,
  type BufferedHeartbeat,
  HEARTBEAT_WINDOW_MS,
} from '#src/utils/activity/activityCollector.ts'

const TAB_ID = crypto.randomUUID()
const FLUSH_INTERVAL_MS = 30_000
const FLUSH_EVENT_THRESHOLD = 60
/** Must match the server schema's `events` `.max()` constraint. */
const MAX_BATCH_SIZE = 500

function buildBatch(
  events: BufferedHeartbeat[],
  appVersion: string
): RecordClinicianActivityRequest {
  return {
    schemaVersion: 1,
    heartbeatWindowMs: HEARTBEAT_WINDOW_MS,
    tabId: TAB_ID,
    appVersion,
    rumSessionId: datadogRum.getInternalContext()?.session_id,
    events,
  } satisfies RecordClinicianActivityRequest
}

export function ClinicianActivityTracker(): null {
  const { seenApi, appVersion } = useSeenApi()
  const medplum = useMedplum()
  const location = useLocation()

  const collectorRef = useRef<ActivityCollector | undefined>(undefined)
  collectorRef.current ??= new ActivityCollector()

  const pathnameRef = useRef<string>(location.pathname)

  const inFlightRef = useRef(false)

  // Close the active window on route change so inputs just before navigation
  // are attributed to the route they were collected on, not the new one.
  useEffect(() => {
    const prev = pathnameRef.current
    if (prev !== location.pathname) {
      collectorRef.current?.closeWindow(prev)
    }
    pathnameRef.current = location.pathname
  }, [location.pathname])

  const flush = useCallback(async () => {
    const collector = collectorRef.current
    if (!collector) {
      return
    }
    if (inFlightRef.current || collector.size === 0) {
      return
    }
    inFlightRef.current = true
    const all = collector.drain()
    const events = all.slice(0, MAX_BATCH_SIZE)
    const overflow = all.slice(MAX_BATCH_SIZE)
    if (overflow.length > 0) {
      collector.requeue(overflow)
    }
    try {
      await seenApi.post('/clinician-activity', buildBatch(events, appVersion))
    } catch {
      collector.requeue(events)
    } finally {
      inFlightRef.current = false
    }
  }, [seenApi, appVersion])

  const flushOnHide = useCallback(() => {
    const collector = collectorRef.current
    if (!collector || collector.size === 0) {
      return
    }
    const token = medplum.getAccessToken()
    if (!token) {
      return
    }
    const all = collector.drain()
    const events = all.slice(0, MAX_BATCH_SIZE)
    const overflow = all.slice(MAX_BATCH_SIZE)
    if (overflow.length > 0) {
      collector.requeue(overflow)
    }
    void fetch(seenApi.getUri({ url: '/clinician-activity' }), {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(buildBatch(events, appVersion)),
      // Only reachable during actual page unload (pagehide).  The JS context
      // is torn down after pagehide fires, so requeue is impossible.  Loss is
      // bounded to one tab's final partial batch and is genuinely unavoidable.
    }).catch(() => {})
  }, [seenApi, medplum, appVersion])

  useEffect(() => {
    const collector = collectorRef.current
    if (!collector) {
      return
    }

    const onPointerDown = (): void => {
      collector.recordInput('click', new Date().toISOString())
    }
    const onKeyDown = (): void => {
      collector.recordInput('key', new Date().toISOString())
    }
    const onOther = (): void => {
      collector.recordInput('other', new Date().toISOString())
    }
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        // Tab backgrounded but JS context is still alive — use the recoverable
        // axios flush so a failed send requeues and retries.
        collector.closeWindow(pathnameRef.current)
        void flush()
      }
    }
    const onPageHide = (): void => {
      // True page unload — JS context tears down after this event.  Keepalive
      // is the only API that can carry the Authorization header past unload;
      // navigator.sendBeacon cannot.  A failed send here is genuinely
      // unrecoverable and bounded to one tab's final partial batch.
      collector.closeWindow(pathnameRef.current)
      flushOnHide()
    }

    window.addEventListener('pointerdown', onPointerDown, {
      passive: true,
      capture: true,
    })
    window.addEventListener('keydown', onKeyDown, {
      passive: true,
      capture: true,
    })
    window.addEventListener('wheel', onOther, { passive: true, capture: true })
    window.addEventListener('scroll', onOther, { passive: true, capture: true })
    window.addEventListener('pointermove', onOther, {
      passive: true,
      capture: true,
    })
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', onPageHide)

    const windowInterval = window.setInterval(() => {
      collector.closeWindow(pathnameRef.current)
      if (collector.size >= FLUSH_EVENT_THRESHOLD) {
        void flush()
      }
    }, HEARTBEAT_WINDOW_MS)

    const flushInterval = window.setInterval(() => {
      void flush()
    }, FLUSH_INTERVAL_MS)

    return () => {
      window.removeEventListener('pointerdown', onPointerDown, {
        capture: true,
      })
      window.removeEventListener('keydown', onKeyDown, { capture: true })
      window.removeEventListener('wheel', onOther, { capture: true })
      window.removeEventListener('scroll', onOther, { capture: true })
      window.removeEventListener('pointermove', onOther, { capture: true })
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', onPageHide)
      clearInterval(windowInterval)
      clearInterval(flushInterval)
    }
  }, [flush, flushOnHide])

  return null
}
